/**
 * Generated by scripts/generate-drizzle-schema.mjs. Do not edit by hand.
 * This product's inputs: scripts/drizzle-schema.config.mjs.
 * Regenerate: bash scripts/client-schema-fresh.sh (data-model-change skill).
 */

import { DrizzleAppSchema, type PowerSyncSQLiteDatabase } from "@powersync/drizzle-driver";
import { index, integer, sqliteTable, text } from "drizzle-orm/sqlite-core";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";

export const activity_recent = sqliteTable(
  "activity_recent",
  {
    id: text("id").primaryKey(),
    scope_id: text("scope_id"),
    list_id: text("list_id"),
    entity_type: text("entity_type"),
    entity_id: text("entity_id"),
    action: text("action"),
    actor_user_id: text("actor_user_id"),
    actor_name: text("actor_name"),
    actor_device_id: text("actor_device_id"),
    platform: text("platform"),
    server_ts: text("server_ts"),
    client_ts: text("client_ts"),
    patch: text("patch"),
    batch_id: text("batch_id"),
    reverts_activity_id: text("reverts_activity_id"),
  },
  (table) => [
    /** Global feed: ORDER BY server_ts DESC */
    index("activity_recent_by_ts").on(table.server_ts),
    /** Transaction history: WHERE entity_type=? AND entity_id=? */
    index("activity_recent_by_entity").on(table.entity_type, table.entity_id, table.server_ts),
  ],
);

export const attachment = sqliteTable(
  "attachment",
  {
    id: text("id").primaryKey(),
    filename: text("filename"),
    content_type: text("content_type"),
    size: integer("size"),
    url: text("url"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    user_id: text("user_id"),
    hash: text("hash"),
    tx_id: text("tx_id"),
  },
  (table) => [
    /** WHERE user_id = ? in getHasLocalDataQuery */
    index("attachment_by_user").on(table.user_id),
    /** WHERE tx_id = ? in transaction receipts */
    index("attachment_by_tx").on(table.tx_id),
  ],
);

export const budget = sqliteTable(
  "budget",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    category_id: text("category_id"),
    amount: integer("amount"),
    period: text("period"),
    start_on: text("start_on"),
    rollover: integer("rollover"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? in useBudgets */
    index("budget_by_user").on(table.user_id),
  ],
);

// Hierarchical categories (parent_id) for expense/income.
export const category = sqliteTable(
  "category",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    parent_id: text("parent_id"),
    kind: text("kind"),
    name: text("name"),
    name_key: text("name_key"),
    icon: text("icon"),
    color: text("color"),
    sort_order: integer("sort_order"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? AND kind = ? to build tree in useCategories */
    index("category_by_user").on(table.user_id, table.kind, table.parent_id, table.sort_order),
  ],
);

export const debt = sqliteTable(
  "debt",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    person: text("person"),
    direction: text("direction"),
    amount: integer("amount"),
    currency: text("currency"),
    note: text("note"),
    due_on: text("due_on"),
    settled: integer("settled"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? AND settled = 0 in useDebts */
    index("debt_by_user").on(table.user_id, table.settled),
  ],
);

export const debt_payment = sqliteTable(
  "debt_payment",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    debt_id: text("debt_id"),
    tx_id: text("tx_id"),
    amount: integer("amount"),
    paid_on: text("paid_on"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE debt_id = ? ORDER BY paid_on DESC in debt payments */
    index("debt_payment_by_debt").on(table.debt_id, table.paid_on),
  ],
);

// Global exchange rates catalogue (id = ISO 4217 code, vs USD pivot). Read-only.
export const exchange_rate = sqliteTable("exchange_rate", {
  id: text("id").primaryKey(),
  rate: text("rate"),
  as_of: text("as_of"),
  updated_at: text("updated_at"),
});

export const font_family = sqliteTable("font_family", {
  id: text("id").primaryKey(),
  family: text("family"),
  name: text("name"),
  category: text("category"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
});

export const font_variant = sqliteTable("font_variant", {
  id: text("id").primaryKey(),
  font_family_id: text("font_family_id"),
  weight: text("weight"),
  url: text("url"),
  created_at: text("created_at"),
  attachment_id: text("attachment_id"),
});

// Small dataset per user — indexes not needed for now.
export const member = sqliteTable("member", {
  id: text("id").primaryKey(),
  organization_id: text("organization_id"),
  user_id: text("user_id"),
  role: text("role"),
  created_at: text("created_at"),
});

// Small dataset — indexes not needed for now.
export const organization = sqliteTable("organization", {
  id: text("id").primaryKey(),
  name: text("name"),
  slug: text("slug"),
  logo: text("logo"),
  metadata: text("metadata"),
  created_at: text("created_at"),
});

export const payee_rule = sqliteTable(
  "payee_rule",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    pattern: text("pattern"),
    category_id: text("category_id"),
    priority: integer("priority"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? ORDER BY priority DESC in auto-categorization */
    index("payee_rule_by_user").on(table.user_id, table.priority),
  ],
);

export const project = sqliteTable(
  "project",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    icon: text("icon"),
    color: text("color"),
    active: integer("active"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? AND active = 1 in useProjects */
    index("project_by_user").on(table.user_id, table.active),
  ],
);

export const recurring_rule = sqliteTable(
  "recurring_rule",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    frequency: text("frequency"),
    interval: integer("interval"),
    next_run_on: text("next_run_on"),
    end_on: text("end_on"),
    last_run_on: text("last_run_on"),
    active: integer("active"),
    template_json: text("template_json"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? AND active = 1 ORDER BY next_run_on in the recurring engine */
    index("recurring_rule_by_user").on(table.user_id, table.active, table.next_run_on),
  ],
);

// Active user sessions — small dataset, no indexes.
export const session = sqliteTable("session", {
  id: text("id").primaryKey(),
});

// Small global dataset (~20 themes) — indexes not needed.
export const themes = sqliteTable("themes", {
  id: text("id").primaryKey(),
  name: text("name"),
  background: text("background"),
  surface: text("surface"),
  accent: text("accent"),
  glow_colors: text("glow_colors"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  is_dark: integer("is_dark"),
  foreground: text("foreground"),
  field_background: text("field_background"),
  danger: text("danger"),
  success: text("success"),
  warning: text("warning"),
  muted: text("muted"),
  pattern_type: text("pattern_type"),
  pattern_opacity: integer("pattern_opacity"),
  pair_id: text("pair_id"),
  font_normal: text("font_normal"),
  font_medium: text("font_medium"),
  font_semibold: text("font_semibold"),
  font_bold: text("font_bold"),
  radius: text("radius"),
  surface_foreground: text("surface_foreground"),
  surface_secondary: text("surface_secondary"),
  surface_secondary_foreground: text("surface_secondary_foreground"),
  surface_tertiary: text("surface_tertiary"),
  surface_tertiary_foreground: text("surface_tertiary_foreground"),
  overlay: text("overlay"),
  overlay_foreground: text("overlay_foreground"),
  default: text("default"),
  default_foreground: text("default_foreground"),
  accent_foreground: text("accent_foreground"),
  field_foreground: text("field_foreground"),
  field_placeholder: text("field_placeholder"),
  field_border: text("field_border"),
  danger_foreground: text("danger_foreground"),
  success_foreground: text("success_foreground"),
  warning_foreground: text("warning_foreground"),
  segment: text("segment"),
  segment_foreground: text("segment_foreground"),
  border: text("border"),
  focus: text("focus"),
  link: text("link"),
  surface_shadow: text("surface_shadow"),
  overlay_shadow: text("overlay_shadow"),
  field_shadow: text("field_shadow"),
  muted_foreground: text("muted_foreground"),
  divider: text("divider"),
  glass_background: text("glass_background"),
  glass_border: text("glass_border"),
  glass_highlight: text("glass_highlight"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
});

// ⚡ Financial transactions: the fastest-growing table. Critical indexes.
export const tx = sqliteTable(
  "tx",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    kind: text("kind"),
    amount: integer("amount"),
    currency: text("currency"),
    base_amount: integer("base_amount"),
    base_currency: text("base_currency"),
    exchange_rate_used: text("exchange_rate_used"),
    wallet_id: text("wallet_id"),
    to_wallet_id: text("to_wallet_id"),
    fee_amount: integer("fee_amount"),
    category_id: text("category_id"),
    occurred_at: text("occurred_at"),
    note: text("note"),
    payee: text("payee"),
    project_id: text("project_id"),
    recurring_rule_id: text("recurring_rule_id"),
    parent_tx_id: text("parent_tx_id"),
    is_adjustment: integer("is_adjustment"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    to_amount: integer("to_amount"),
    cleared: integer("cleared"),
  },
  (table) => [
    /** WHERE user_id = ? ORDER BY occurred_at DESC in daily/monthly view */
    index("tx_by_user_date").on(table.user_id, table.occurred_at),
    /** Account balance aggregation in useAccountBalances */
    index("tx_by_wallet").on(table.wallet_id, table.deleted_at),
    /** Incoming transfers in useAccountBalances */
    index("tx_by_to_wallet").on(table.to_wallet_id, table.deleted_at),
    /** WHERE category_id = ? in reports and budget progress */
    index("tx_by_category").on(table.category_id, table.occurred_at),
    /** WHERE project_id = ? ORDER BY occurred_at DESC in project totals */
    index("tx_by_project").on(table.project_id, table.occurred_at),
  ],
);

export const tx_template = sqliteTable(
  "tx_template",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    sort_order: integer("sort_order"),
    template_json: text("template_json"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? ORDER BY sort_order in useTemplates */
    index("tx_template_by_user").on(table.user_id, table.sort_order),
  ],
);

// Only 1 row per user — indexes not needed.
export const user = sqliteTable("user", {
  id: text("id").primaryKey(),
  name: text("name"),
  email: text("email"),
  email_verified: integer("email_verified"),
  image: text("image"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  locale: text("locale"),
  global_theme_id: text("global_theme_id"),
  is_blur: integer("is_blur"),
  font_family: text("font_family"),
  follow_device_theme: integer("follow_device_theme"),
  role: text("role"),
  base_currency: text("base_currency"),
  month_start_day: integer("month_start_day"),
});

// User exchange rates by date (offline-editable). Each row sets
// `rate = units of base_currency per 1 currency` effective SINCE `as_of`.
export const user_exchange_rate = sqliteTable(
  "user_exchange_rate",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    currency: text("currency"),
    base_currency: text("base_currency"),
    rate: text("rate"),
    as_of: text("as_of"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? ORDER BY as_of in conversion by effective date */
    index("user_exchange_rate_by_user").on(
      table.user_id,
      table.currency,
      table.base_currency,
      table.as_of,
    ),
  ],
);

// User financial accounts (the account table is for OAuth).
export const wallet = sqliteTable(
  "wallet",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    kind: text("kind"),
    currency: text("currency"),
    initial_balance: integer("initial_balance"),
    icon: text("icon"),
    color: text("color"),
    sort_order: integer("sort_order"),
    group_id: text("group_id"),
    archived: integer("archived"),
    exclude_from_totals: integer("exclude_from_totals"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? AND archived = 0 ORDER BY sort_order in useAccounts */
    index("wallet_by_user").on(table.user_id, table.archived, table.sort_order),
  ],
);

export const wallet_group = sqliteTable(
  "wallet_group",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    sort_order: integer("sort_order"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? ORDER BY sort_order in useAccountGroups */
    index("wallet_group_by_user").on(table.user_id, table.sort_order),
  ],
);

export const drizzleSchema = {
  activity_recent,
  attachment,
  budget,
  category,
  debt,
  debt_payment,
  exchange_rate,
  font_family,
  font_variant,
  member,
  organization,
  payee_rule,
  project,
  recurring_rule,
  session,
  themes,
  tx,
  tx_template,
  user,
  user_exchange_rate,
  wallet,
  wallet_group,
};

export const AppSchema = new DrizzleAppSchema(drizzleSchema);

export type Database = (typeof AppSchema)["types"];

/**
 * Type helper for the Drizzle database instance wrapped with PowerSync.
 */
export type PowerSyncDrizzleDB = PowerSyncSQLiteDatabase<typeof drizzleSchema>;

export const USER_DATA_TABLES = [
  "attachment",
  "budget",
  "category",
  "debt",
  "debt_payment",
  "payee_rule",
  "project",
  "recurring_rule",
  "tx",
  "tx_template",
  "user_exchange_rate",
  "wallet",
  "wallet_group",
] as const;

export type ActivityRecent = InferSelectModel<typeof activity_recent>;
export type ActivityRecentInsert = InferInsertModel<typeof activity_recent>;
export type ActivityRecentRecord = ActivityRecent;
export type Attachment = InferSelectModel<typeof attachment>;
export type AttachmentInsert = InferInsertModel<typeof attachment>;
export type AttachmentRecord = Attachment;
export type Budget = InferSelectModel<typeof budget>;
export type BudgetInsert = InferInsertModel<typeof budget>;
export type BudgetRecord = Budget;
export type Category = InferSelectModel<typeof category>;
export type CategoryInsert = InferInsertModel<typeof category>;
export type CategoryRecord = Category;
export type Debt = InferSelectModel<typeof debt>;
export type DebtInsert = InferInsertModel<typeof debt>;
export type DebtRecord = Debt;
export type DebtPayment = InferSelectModel<typeof debt_payment>;
export type DebtPaymentInsert = InferInsertModel<typeof debt_payment>;
export type DebtPaymentRecord = DebtPayment;
export type ExchangeRate = InferSelectModel<typeof exchange_rate>;
export type ExchangeRateInsert = InferInsertModel<typeof exchange_rate>;
export type ExchangeRateRecord = ExchangeRate;
export type FontFamily = InferSelectModel<typeof font_family>;
export type FontFamilyInsert = InferInsertModel<typeof font_family>;
export type FontFamilyRecord = FontFamily;
export type FontVariant = InferSelectModel<typeof font_variant>;
export type FontVariantInsert = InferInsertModel<typeof font_variant>;
export type FontVariantRecord = FontVariant;
export type Member = InferSelectModel<typeof member>;
export type MemberInsert = InferInsertModel<typeof member>;
export type MemberRecord = Member;
export type Organization = InferSelectModel<typeof organization>;
export type OrganizationInsert = InferInsertModel<typeof organization>;
export type OrganizationRecord = Organization;
export type PayeeRule = InferSelectModel<typeof payee_rule>;
export type PayeeRuleInsert = InferInsertModel<typeof payee_rule>;
export type PayeeRuleRecord = PayeeRule;
export type Project = InferSelectModel<typeof project>;
export type ProjectInsert = InferInsertModel<typeof project>;
export type ProjectRecord = Project;
export type RecurringRule = InferSelectModel<typeof recurring_rule>;
export type RecurringRuleInsert = InferInsertModel<typeof recurring_rule>;
export type RecurringRuleRecord = RecurringRule;
export type Session = InferSelectModel<typeof session>;
export type SessionInsert = InferInsertModel<typeof session>;
export type SessionRecord = Session;
export type Themes = InferSelectModel<typeof themes>;
export type ThemesInsert = InferInsertModel<typeof themes>;
export type ThemesRecord = Themes;
export type Tx = InferSelectModel<typeof tx>;
export type TxInsert = InferInsertModel<typeof tx>;
export type TxRecord = Tx;
export type TxTemplate = InferSelectModel<typeof tx_template>;
export type TxTemplateInsert = InferInsertModel<typeof tx_template>;
export type TxTemplateRecord = TxTemplate;
export type User = InferSelectModel<typeof user>;
export type UserInsert = InferInsertModel<typeof user>;
export type UserRecord = User;
export type UserExchangeRate = InferSelectModel<typeof user_exchange_rate>;
export type UserExchangeRateInsert = InferInsertModel<typeof user_exchange_rate>;
export type UserExchangeRateRecord = UserExchangeRate;
export type Wallet = InferSelectModel<typeof wallet>;
export type WalletInsert = InferInsertModel<typeof wallet>;
export type WalletRecord = Wallet;
export type WalletGroup = InferSelectModel<typeof wallet_group>;
export type WalletGroupInsert = InferInsertModel<typeof wallet_group>;
export type WalletGroupRecord = WalletGroup;
